import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Disc3, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const OWNER_EMAIL = "platformsoundsdesignlal@gmail.com";

const OwnerLogin = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [step, setStep] = useState("request"); // request | verify
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-code", { email: OWNER_EMAIL });
      setStep("verify");
      if (data.channel === "email") {
        toast.success("Code sent to your email");
      } else {
        setDevCode(data.dev_code || "");
        toast.message("Email service not configured yet", { description: "Use the code shown below to continue." });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send code");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email: OWNER_EMAIL, code });
      localStorage.setItem("nx_token", data.token);
      updateUser(data.user);
      toast.success("Welcome back, owner");
      navigate("/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-zinc-900/60 border border-white/10 text-white placeholder-zinc-500 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]";

  return (
    <div className="min-h-[calc(100vh-6.5rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <Disc3 className="h-7 w-7 text-[#FFD60A]" />
          <span className="font-brand text-2xl text-[#FFD60A]">NEXTtt<sup className="text-[0.5em] align-super ml-0.5">™</sup></span>
        </div>
        <h1 className="font-display text-2xl text-white flex items-center gap-2"><KeyRound className="h-6 w-6 text-[#FFD60A]" /> Owner sign-in</h1>
        <p className="text-zinc-400 mt-2 mb-8 text-sm">A one-time code is sent to the owner email <span className="text-zinc-200">{OWNER_EMAIL}</span>.</p>

        {step === "request" ? (
          <button onClick={requestCode} disabled={busy} data-testid="request-code-button" className="w-full bg-[#FFD60A] text-black py-3 font-semibold hover:bg-[#FFD60A]/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            <Mail className="h-4 w-4" /> {busy ? "Sending…" : "Send me a code"}
          </button>
        ) : (
          <form onSubmit={verify} className="space-y-4">
            {devCode && (
              <div className="bg-[#FFD60A]/10 border border-[#FFD60A]/40 text-[#FFD60A] text-sm px-4 py-3" data-testid="dev-code">
                Your code: <span className="font-mono font-bold tracking-widest">{devCode}</span>
              </div>
            )}
            <input
              data-testid="code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              className={`${inputCls} text-center tracking-[0.5em] text-lg`}
              required
            />
            <button type="submit" disabled={busy} data-testid="verify-code-button" className="w-full bg-[#FFD60A] text-black py-3 font-semibold hover:bg-[#FFD60A]/90 transition-colors disabled:opacity-60">
              {busy ? "Verifying…" : "Verify & sign in"}
            </button>
            <button type="button" onClick={requestCode} className="w-full text-sm text-zinc-400 hover:text-white">Resend code</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OwnerLogin;
